import { useMemo, useState } from 'react';
import {
  customisedPriceMinor,
  type CustomerAddonGroup,
  type CustomerDish,
} from '@capsico/contracts';

export interface GroupRule {
  group: CustomerAddonGroup;
  min: number;
  max: number;
  chosen: string[];
  ok: boolean;
}

export interface SlotRule {
  id: string;
  name: string;
  pick: number;
  options: CustomerDish['comboSlots'][number]['options'];
  chosen: string[];
  ok: boolean;
}

/**
 * Customisation sheet state: size (variant), add-on groups with min / max, combo choices and
 * quantity, with the live total. Pure UI state — nothing is sent until the cart arrives (W10).
 */
export function useCustomise(dish: CustomerDish, addonGroups: CustomerAddonGroup[]) {
  const defaultVariant = dish.variants.find((v) => v.isDefault)?.id ?? dish.variants[0]?.id ?? null;
  const [variantId, setVariantId] = useState<string | null>(defaultVariant);
  const [picks, setPicks] = useState<Record<string, string[]>>({});
  const [slots, setSlots] = useState<Record<string, string[]>>({});
  const [quantity, setQuantity] = useState(1);

  const groups = useMemo<GroupRule[]>(
    () =>
      dish.addonGroupIds
        .map((id) => addonGroups.find((group) => group.id === id))
        .filter((group): group is CustomerAddonGroup => group !== undefined)
        .map((group) => {
          const chosen = picks[group.id] ?? [];
          const min = group.minSelect;
          const max = group.selection === 'one' ? 1 : group.maxSelect;
          return { group, min, max, chosen, ok: chosen.length >= min && chosen.length <= max };
        }),
    [dish.addonGroupIds, addonGroups, picks],
  );

  const comboSlots = useMemo<SlotRule[]>(
    () =>
      dish.comboSlots.map((slot) => {
        const chosen = slots[slot.id] ?? [];
        return { ...slot, chosen, ok: chosen.length === slot.pick };
      }),
    [dish.comboSlots, slots],
  );

  const addonPrices = groups.flatMap((rule) =>
    rule.chosen.map((id) => rule.group.options.find((option) => option.id === id)?.priceMinor ?? 0),
  );
  const total = customisedPriceMinor({ dish, variantId, addonOptionPrices: addonPrices, quantity });

  const togglePick = (rule: GroupRule, optionId: string) => {
    setPicks((current) => {
      const now = current[rule.group.id] ?? [];
      if (rule.max === 1)
        return {
          ...current,
          [rule.group.id]: now.includes(optionId) && rule.min === 0 ? [] : [optionId],
        };
      if (now.includes(optionId))
        return { ...current, [rule.group.id]: now.filter((id) => id !== optionId) };
      if (now.length >= rule.max) return current;
      return { ...current, [rule.group.id]: [...now, optionId] };
    });
  };

  const toggleSlot = (slot: SlotRule, itemId: string) => {
    setSlots((current) => {
      const now = current[slot.id] ?? [];
      if (slot.pick === 1) return { ...current, [slot.id]: [itemId] };
      if (now.includes(itemId)) return { ...current, [slot.id]: now.filter((id) => id !== itemId) };
      if (now.length >= slot.pick) return current;
      return { ...current, [slot.id]: [...now, itemId] };
    });
  };

  return {
    variantId,
    setVariantId,
    groups,
    togglePick,
    comboSlots,
    toggleSlot,
    quantity,
    increase: () => setQuantity((q) => Math.min(20, q + 1)),
    decrease: () => setQuantity((q) => Math.max(1, q - 1)),
    total,
    complete: groups.every((rule) => rule.ok) && comboSlots.every((slot) => slot.ok),
  };
}
